import { Message } from "../../services/chatService/ChatService";

export const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000;

export interface MessageGroupingPosition {
  groupedWithPrevious: boolean;
  groupedWithNext: boolean;
}

const isRenderableUserMessage = (message: Message): boolean =>
  Boolean(message.author) &&
  message.messageType !== "SYSTEM" &&
  message.messageType !== "CHANNEL_EVENT" &&
  !message.isDeleted;

/**
 * Slack-style visual grouping for consecutive messages. A group continues only
 * while the author is unchanged and each message arrives within five minutes
 * of the previous one. Day, thread, deletion, and system-event boundaries
 * always start a new visual group.
 */
export function getMessageGroupingPositions(
  messages: Message[],
  windowMs = MESSAGE_GROUP_WINDOW_MS,
): MessageGroupingPosition[] {
  const canGroup = (previous: Message | undefined, current: Message | undefined): boolean => {
    if (!previous || !current || !isRenderableUserMessage(previous) || !isRenderableUserMessage(current)) return false;
    if (previous.author?.id !== current.author?.id) return false;
    if ((previous.threadRootMessageId ?? previous.parentMessageId ?? null) !== (current.threadRootMessageId ?? current.parentMessageId ?? null)) return false;

    const previousDate = new Date(previous.createDate);
    const currentDate = new Date(current.createDate);
    if (Number.isNaN(previousDate.getTime()) || Number.isNaN(currentDate.getTime())) return false;
    if (!areSameDay(previousDate, currentDate)) return false;

    const elapsed = currentDate.getTime() - previousDate.getTime();
    return elapsed >= 0 && elapsed <= windowMs;
  };

  return messages.map((message, index) => ({
    groupedWithPrevious: canGroup(messages[index - 1], message),
    groupedWithNext: canGroup(message, messages[index + 1]),
  }));
}

/**
 * Helper function to check if two dates are on the same day.
 */
function areSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Helper function to format a date as "MMM d, yyyy" (e.g., "Mar 1, 2025").
 */
function formatDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
}

/**
 * Group messages by day.
 * - If the date is "today," we further split into multiple groups whenever there’s
 *   more than 5 hours between consecutive messages.
 * - Otherwise, all messages of that day go into a single group.
 */

export function groupMessagesByDate(messages: Message[]): {
  dateLabel: string;
  messages: Message[];
}[] {
  if (!messages?.length) return [];

  // Sort messages by ascending createDate
  const sorted = [...messages].sort(
    (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
  );

  const now = new Date();
  const groups: { dateLabel: string; messages: Message[] }[] = [];

  // Use a Map to group by label
  const groupMap = new Map<string, Message[]>();

  for (const msg of sorted) {
    const msgDate = new Date(msg.createDate);
    const isMsgToday = areSameDay(msgDate, now);
    const dateLabel = isMsgToday ? "Today" : formatDate(msgDate);

    if (!groupMap.has(dateLabel)) {
      groupMap.set(dateLabel, []);
    }
    groupMap.get(dateLabel)!.push(msg);
  }

  // Convert Map to array in order
  Array.from(groupMap.entries()).forEach(([dateLabel, msgs]) => {
    groups.push({ dateLabel, messages: msgs });
  });

  return groups;
}
